import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import {
  CharlieQrCode,
  getCharlieQrModuleCount,
} from '@ver.id/libs.ui.charlie';
import { useQrFitsCard } from '../../hooks/useQrFitsCard';

/** Shown faded behind "Refresh QR"; never scanned. */
const EXPIRED_PLACEHOLDER = 'https://ver.id/expired';

/**
 * The code is drawn at 3px per module including a 4-module quiet zone, kept
 * between 188px (a by-reference link) and 270px (a dense inline request).
 * When long text needs the room, it may shrink to 2px per module.
 */
function qrCodeSizes(data: string, image?: string) {
  let modules = 53;
  try {
    modules = getCharlieQrModuleCount(data, image);
  } catch {
    /* too long for any QR version: use the maximum */
  }
  const ideal = Math.max(188, Math.min(270, (modules + 8) * 3));
  return { ideal, min: Math.min(ideal, (modules + 8) * 2) };
}

type Props = {
  data: string;
  logo?: string;
  label: string;
  /** Faded code that is itself the "Refresh code" button. */
  expired?: { label: string; onRefresh: () => void };
};

/**
 * EmbeddedQrCodeCard — The scan code: always dark on white in a white box, also on dark
 * websites, with the app's logo in the centre.
 */
export default function EmbeddedQrCodeCard({
  data,
  logo,
  label,
  expired,
}: Props) {
  const sizes = qrCodeSizes(expired ? EXPIRED_PLACEHOLDER : data, logo);
  const { ref, size } = useQrFitsCard(sizes.ideal, sizes.min);
  const box =
    'relative flex w-full shrink-0 items-center justify-center rounded-[10px] border border-border bg-white leading-none text-[#0a0a0a]';

  if (expired) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={expired.onRefresh}
        className={`${box} group`}
        style={{ height: size + 16 }}
      >
        <span aria-hidden="true" className="opacity-[0.12] blur-[2px]">
          <CharlieQrCode data={EXPIRED_PLACEHOLDER} size={size} />
        </span>
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm leading-5 font-semibold">
          <span
            aria-hidden="true"
            className="inline-flex size-11 items-center justify-center rounded-full bg-[#0a0a0a] text-[18px] text-white transition-transform group-hover:scale-110 motion-reduce:transition-none"
          >
            <FontAwesomeIcon icon={faArrowsRotate} />
          </span>
          <span className="group-hover:underline">{expired.label}</span>
        </span>
      </button>
    );
  }

  return (
    <div
      ref={ref}
      role="img"
      aria-label={label}
      className={box}
      style={{ height: size + 16 }}
    >
      <CharlieQrCode
        data={data}
        image={logo}
        size={size}
        testId="chulak-qr-code"
      />
    </div>
  );
}
